import { ContributionsFilterState, DeliveryStateKey } from './contributions-filters.types';

/**
 * How each delivery state looks, and the order of the quick filters.
 *
 * **Which state a delivery is in is not decided here.** The backend owns the
 * conditions and their precedence (`App\Enums\DeliveryState`) and serves each
 * delivery's `state` and `states`; a quick filter asks the backend for
 * `filters.state` — the Ideas plan, open question 12.6, closed 2026-10-04.
 * Until then this file held the conditions, and the frontend could never show
 * "reopened", because the API did not send `reopen_count`.
 */
export const CONTRIBUTIONS_FILTER_STATES: ContributionsFilterState[] = [
  {
    key: 'awaiting_attention',
    label: 'Awaiting your attention',
    translationKey: 'tracking.awaiting_attention',
    translationScope: undefined, // No scope needed for translations in 'tracking'
    filters: { state: 'awaiting_attention' },
    iconClasses: 'fa-solid fa-circle',
    badgeClasses: 'badge-info',
    isDefault: true
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    translationKey: 'tracking.in_progress',
    translationScope: undefined,
    filters: { state: 'in_progress' },
    iconClasses: 'fa-solid fa-arrow-rotate-right fa-spin',
    badgeClasses: 'badge-warning'
  },
  {
    key: 'under_review',
    label: 'Under review',
    translationKey: 'contributions.editorial.under_review',
    translationScope: 'contributions',
    filters: { state: 'under_review' },
    iconClasses: 'fa-solid fa-book-open-reader',
    badgeClasses: 'badge-info'
  },
  {
    key: 'reopened',
    label: 'Reopened',
    translationKey: 'tracking.reopened',
    translationScope: undefined,
    filters: { state: 'reopened' },
    iconClasses: 'fa-solid fa-arrow-rotate-right fa-spin',
    badgeClasses: 'badge-warning'
  },
  {
    key: 'abandoned',
    label: 'Abandoned',
    translationKey: 'tracking.abandoned',
    translationScope: undefined,
    filters: { state: 'abandoned' },
    iconClasses: 'fa-solid fa-ban',
    badgeClasses: 'badge-secondary'
  },
];

/**
 * Get the default filter state
 * This is the filter state marked as default in the configuration
 */
export const DEFAULT_CONTRIBUTIONS_FILTER_STATE = CONTRIBUTIONS_FILTER_STATES.find(
  state => state.isDefault === true
)!;

/**
 * Get default contributions filters
 * Returns the filters from the default filter state
 */
export function getDefaultContributionsFilters() {
  return { ...DEFAULT_CONTRIBUTIONS_FILTER_STATE.filters };
}

/**
 * Find a filter state by its key
 */
export function getFilterStateByKey(key: string): ContributionsFilterState | undefined {
  return CONTRIBUTIONS_FILTER_STATES.find(state => state.key === key);
}

/**
 * The presentations of every state the backend says a delivery matches, in its order.
 */
export function getFilterStatesForDelivery(delivery: { states?: DeliveryStateKey[] } | null | undefined): ContributionsFilterState[] {
  return (delivery?.states ?? [])
    .map(key => getFilterStateByKey(key))
    .filter((state): state is ContributionsFilterState => !!state);
}
